import { Badge, BadgeVariant } from '@moment/design-system-ui/badge'
import {
  FitchRating,
  MoodysRating,
  SPRating,
} from '@moment/trading-types/reference-data'
import { useMemo } from 'react'

import { PLACEHOLDER } from '@/utils/presentation/formatting'

interface RatingBadgeProps {
  spRating?: SPRating
  moodysRating?: MoodysRating
  fitchRating?: FitchRating
  spAccess?: boolean
  moodysAccess?: boolean
  fitchAccess?: boolean
}

const spRatingVariantMap: Record<SPRating, BadgeVariant> = {
  // investment grade
  [SPRating.AAA]: BadgeVariant.Success,
  [SPRating.AA_PLUS]: BadgeVariant.Success,
  [SPRating.AA]: BadgeVariant.Success,
  [SPRating.AA_MINUS]: BadgeVariant.Success,
  [SPRating.A_PLUS]: BadgeVariant.Success,
  [SPRating.A]: BadgeVariant.Success,
  [SPRating.A_MINUS]: BadgeVariant.Success,
  [SPRating.BBB_PLUS]: BadgeVariant.Success,
  [SPRating.BBB]: BadgeVariant.Success,
  [SPRating.BBB_MINUS]: BadgeVariant.Success,
  // high yield
  [SPRating.BB_PLUS]: BadgeVariant.Warning,
  [SPRating.BB]: BadgeVariant.Warning,
  [SPRating.BB_MINUS]: BadgeVariant.Warning,
  [SPRating.B_PLUS]: BadgeVariant.Warning,
  [SPRating.B]: BadgeVariant.Warning,
  [SPRating.B_MINUS]: BadgeVariant.Warning,
  [SPRating.CCC_PLUS]: BadgeVariant.Warning,
  [SPRating.CCC]: BadgeVariant.Warning,
  [SPRating.CCC_MINUS]: BadgeVariant.Warning,
  [SPRating.CC]: BadgeVariant.Warning,
  [SPRating.C]: BadgeVariant.Warning,
  [SPRating.D]: BadgeVariant.Warning,
  // not rated
  [SPRating.NR]: BadgeVariant.Neutral,
}

const moodysRatingVariantMap: Record<MoodysRating, BadgeVariant> = {
  // investment grade
  [MoodysRating.Aaa]: BadgeVariant.Success,
  [MoodysRating.Aa1]: BadgeVariant.Success,
  [MoodysRating.Aa2]: BadgeVariant.Success,
  [MoodysRating.Aa3]: BadgeVariant.Success,
  [MoodysRating.A1]: BadgeVariant.Success,
  [MoodysRating.A2]: BadgeVariant.Success,
  [MoodysRating.A3]: BadgeVariant.Success,
  [MoodysRating.Baa1]: BadgeVariant.Success,
  [MoodysRating.Baa2]: BadgeVariant.Success,
  [MoodysRating.Baa3]: BadgeVariant.Success,
  // high yield
  [MoodysRating.Ba1]: BadgeVariant.Warning,
  [MoodysRating.Ba2]: BadgeVariant.Warning,
  [MoodysRating.Ba3]: BadgeVariant.Warning,
  [MoodysRating.B1]: BadgeVariant.Warning,
  [MoodysRating.B2]: BadgeVariant.Warning,
  [MoodysRating.B3]: BadgeVariant.Warning,
  [MoodysRating.Caa1]: BadgeVariant.Warning,
  [MoodysRating.Caa2]: BadgeVariant.Warning,
  [MoodysRating.Caa3]: BadgeVariant.Warning,
  [MoodysRating.Ca]: BadgeVariant.Warning,
  [MoodysRating.C]: BadgeVariant.Warning,
}

const fitchRatingVariantMap: Record<FitchRating, BadgeVariant> = {
  // investment grade
  [FitchRating.AAA]: BadgeVariant.Success,
  [FitchRating.AA_PLUS]: BadgeVariant.Success,
  [FitchRating.AA]: BadgeVariant.Success,
  [FitchRating.AA_MINUS]: BadgeVariant.Success,
  [FitchRating.A_PLUS]: BadgeVariant.Success,
  [FitchRating.A]: BadgeVariant.Success,
  [FitchRating.A_MINUS]: BadgeVariant.Success,
  [FitchRating.BBB_PLUS]: BadgeVariant.Success,
  [FitchRating.BBB]: BadgeVariant.Success,
  [FitchRating.BBB_MINUS]: BadgeVariant.Success,
  // high yield
  [FitchRating.BB_PLUS]: BadgeVariant.Warning,
  [FitchRating.BB]: BadgeVariant.Warning,
  [FitchRating.BB_MINUS]: BadgeVariant.Warning,
  [FitchRating.B_PLUS]: BadgeVariant.Warning,
  [FitchRating.B]: BadgeVariant.Warning,
  [FitchRating.B_MINUS]: BadgeVariant.Warning,
  [FitchRating.CCC_PLUS]: BadgeVariant.Warning,
  [FitchRating.CCC]: BadgeVariant.Warning,
  [FitchRating.CCC_MINUS]: BadgeVariant.Warning,
  [FitchRating.CC]: BadgeVariant.Warning,
  [FitchRating.C]: BadgeVariant.Warning,
  [FitchRating.D]: BadgeVariant.Warning,
  // not rated
  [FitchRating.NR]: BadgeVariant.Neutral,
}

export function RatingBadge({
  spRating,
  moodysRating,
  fitchRating,
  spAccess,
  moodysAccess,
  fitchAccess,
}: RatingBadgeProps) {
  const displayText = useMemo(() => {
    const formatRating = (rating: string | undefined): string => {
      if (!rating) return PLACEHOLDER
      const upperRating = rating.toUpperCase()
      if (upperRating === 'NR' || upperRating === 'WR') {
        return PLACEHOLDER
      }
      return rating
    }

    const formattedMoodys = formatRating(moodysRating)
    const formattedSP = formatRating(spRating)
    const formattedFitch = formatRating(fitchRating)

    const parts: string[] = []

    if (moodysAccess || moodysRating) {
      parts.push(formattedMoodys)
    }

    if (spAccess || spRating) {
      parts.push(formattedSP)
    }

    if (fitchAccess || fitchRating) {
      parts.push(formattedFitch)
    }

    if (parts.length === 0) {
      return PLACEHOLDER
    }

    return parts.join(' / ')
  }, [spRating, moodysRating, fitchRating, spAccess, moodysAccess, fitchAccess])

  // Determine variant based on available ratings
  const variant = useMemo(() => {
    // Check if rating is NR/WR
    const isNotRated = (rating: string | undefined): boolean => {
      if (!rating) return true
      const upperRating = rating.toUpperCase()
      return upperRating === 'NR' || upperRating === 'WR'
    }

    // Use S&P variant if available and not NR/WR
    if (spRating && !isNotRated(spRating)) {
      return spRatingVariantMap[spRating] || BadgeVariant.Neutral
    }

    // Otherwise use Moody's variant if available and not NR/WR
    if (moodysRating && !isNotRated(moodysRating)) {
      return moodysRatingVariantMap[moodysRating] || BadgeVariant.Neutral
    }

    // Otherwise use Fitch variant if available and not NR/WR
    if (fitchRating && !isNotRated(fitchRating)) {
      return fitchRatingVariantMap[fitchRating] || BadgeVariant.Neutral
    }

    // Default to neutral for no ratings or NR/WR
    return BadgeVariant.Neutral
  }, [spRating, moodysRating, fitchRating])

  return (
    <Badge className="capitalize" variant={variant}>
      {displayText}
    </Badge>
  )
}
